import React, { useState } from 'react';
import { Tag, Input, Select, Row, Col } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import type { ColumnsType } from 'antd/es/table';
import { useBidvRefundBanks } from '../../hooks/useBidv';
import { DataTable } from '../../components';
import type { BidvRefundBank } from '../../services/bidv.service';

const BidvList: React.FC = () => {
  const navigate = useNavigate();
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState<number | undefined>();
  const [tenantFilter, setTenantFilter] = useState<string>('');

  const { refundBanks, loading, meta, setFilters, refetch, filters } = useBidvRefundBanks();

  const applyFilters = (overrides: Partial<typeof filters> = {}) => {
    setFilters({
      ...filters,
      page: 1,
      q: searchText || undefined,
      status: statusFilter,
      tenant_id: tenantFilter || undefined,
      ...overrides,
    });
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    applyFilters({ q: value || undefined });
  };

  const handlePageChange = (page: number, pageSize: number) => {
    setFilters({ ...filters, page, limit: pageSize });
  };

  const columns: ColumnsType<BidvRefundBank> = [
    {
      title: 'Tên ngân hàng',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => <a onClick={() => navigate(`/bidv-refund-banks/${record.bank_id}`)}>{name}</a>,
    },
    { title: 'Mã', dataIndex: 'code', key: 'code', width: 140 },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 130,
      render: (s: number) => <Tag color={s === 1 ? 'success' : 'default'}>{s === 1 ? 'Bật' : 'Tắt'}</Tag>,
    },
    { title: 'Thứ tự', dataIndex: 'ordering', key: 'ordering', width: 90 },
    {
      title: 'Tenant',
      dataIndex: 'tenant_id',
      key: 'tenant_id',
      width: 200,
      render: (id?: string) => id ? <code style={{ fontSize: 11 }}>{id.slice(-8)}</code> : '—',
    },
  ];

  const filterContent = (
    <Row gutter={16}>
      <Col span={8}>
        <Input.Search
          placeholder="Tenant ID"
          allowClear
          value={tenantFilter}
          onChange={(e) => setTenantFilter(e.target.value)}
          onSearch={() => applyFilters()}
        />
      </Col>
      <Col span={6}>
        <Select
          placeholder="Trạng thái"
          allowClear
          style={{ width: '100%' }}
          value={statusFilter}
          onChange={(v) => { setStatusFilter(v); applyFilters({ status: v }); }}
          options={[
            { value: 1, label: 'Bật' },
            { value: 0, label: 'Tắt' },
          ]}
        />
      </Col>
    </Row>
  );

  return (
    <DataTable<BidvRefundBank>
      title="BIDV refund bank (cross-tenant)"
      data={refundBanks}
      columns={columns}
      loading={loading}
      rowKey="bank_id"
      searchable
      searchPlaceholder="Tên / mã..."
      onSearch={handleSearch}
      onRefresh={refetch}
      filters={filterContent}
      pagination={{
        current: meta.page,
        pageSize: meta.limit,
        total: meta.total,
        onChange: handlePageChange,
      }}
      onRowClick={(record) => navigate(`/bidv-refund-banks/${record.bank_id}`)}
      actionColumn={{
        width: 80,
        fixed: 'right',
        actions: (record) => [
          {
            key: 'view',
            label: 'Xem chi tiết',
            icon: <EyeOutlined />,
            onClick: () => navigate(`/bidv-refund-banks/${record.bank_id}`),
          },
        ],
      }}
    />
  );
};

export default BidvList;
export { BidvList };
